import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, Spinner } from '../../components/ui';
import { 
  Building, Users, DollarSign, Smartphone, 
  Activity, TrendingUp, TrendingDown, AlertCircle, CheckCircle,
 Clock, Shield, RefreshCw, Zap, LayoutDashboard
} from 'lucide-react';
import './Admin.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { request } = useApi();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/admin/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  const statCards = [
    { 
      icon: Building, 
      label: 'Total Organizations', 
      value: stats?.totalOrganizations || 0,
      color: '#8B5CF6',
      bg: 'rgba(139,92,246,0.1)',
      trend: '+12%',
      trendUp: true
    },
    { 
      icon: Users, 
      label: 'Total Users', 
      value: stats?.totalUsers || 0,
      color: '#3B82F6',
      bg: 'rgba(59,130,246,0.1)',
      trend: '+8%',
      trendUp: true
    },
    { 
      icon: DollarSign, 
      label: 'Total Credits', 
      value: stats?.totalCredits || 0,
      color: '#F59E0B',
      bg: 'rgba(245,158,11,0.1)',
      trend: '+5%',
      trendUp: true
    },
    { 
      icon: Smartphone, 
      label: 'WA Active Accounts', 
      value: stats?.activeWaAccounts || 0,
      color: '#10B981',
      bg: 'rgba(16,185,129,0.1)',
      trend: '+3%',
      trendUp: true
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Admin Dashboard</h1>
          <p className="admin-subtitle">System overview and key metrics</p>
        </div>
        <button className="refresh-btn" onClick={fetchStats}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="admin-stats-grid">
        {statCards.map((card, idx) => (
          <div className="admin-stat-card" key={idx}>
            <div className="admin-stat-icon" style={{ background: card.bg, color: card.color }}>
              <card.icon size={22} />
            </div>
            <div className="admin-stat-info">
              <div className="admin-stat-value">{card.value.toLocaleString()}</div>
              <div className="admin-stat-label">{card.label}</div>
            </div>
            <div className={`admin-stat-trend ${card.trendUp ? 'up' : 'down'}`}>
              {card.trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {card.trend}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Status Cards */}
      <div className="quick-actions-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="quick-action-card">
          <div className="quick-action-icon green">
            <CheckCircle size={20} />
          </div>
          <div>
            <h4>Active Organizations</h4>
            <p className="quick-action-value">{stats?.activeOrganizations || 0}</p>
          </div>
        </div>
        <div className="quick-action-card">
          <div className="quick-action-icon orange">
            <Clock size={20} />
          </div>
          <div>
            <h4>Pending Approval</h4>
            <p className="quick-action-value">{stats?.pendingOrganizations || 0}</p>
          </div>
        </div>
        <div className="quick-action-card">
          <div className="quick-action-icon red">
            <AlertCircle size={20} />
          </div>
          <div>
            <h4>Suspended</h4>
            <p className="quick-action-value">{stats?.suspendedOrganizations || 0}</p>
          </div>
        </div>
        <div className="quick-action-card">
          <div className="quick-action-icon purple">
            <Shield size={20} />
          </div>
          <div>
            <h4>WA Onboarded</h4>
            <p className="quick-action-value">{stats?.whatsappOnboarded || 0}</p>
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div style={{ marginTop: '24px', textAlign: 'center', padding: '16px', background: 'rgba(139,92,246,0.05)', borderRadius: '12px' }}>
        <span style={{ fontSize: '12px', color: '#94A3B8' }}>
          <Activity size={14} style={{ display: 'inline', marginRight: '4px' }} />
          System Health: <span style={{ color: '#10B981', fontWeight: 600 }}>Operational</span> 
          | Last updated: {new Date().toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default AdminDashboard;